import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Button, Parts, Stars } from '../../ui';
import { ease, prefersReducedMotion } from '../../lib/motion';
import { md } from '../../lib/md';
import { sound } from '../../lib/sound';
import { useStrings } from '../../state/useLocale';
import { FREE_HINTS, starsFor } from '../../progress';
import type { HintLadderProps } from '../contracts';

/**
 * Help, on request, and the half of it that has a price.
 *
 * Behind this button is the five-rung ladder the product is built on: a
 * question, then where to look, then which command to look with, then the
 * concept, and only last the answer. The learner never sees that structure.
 * Listing five rungs with four greyed out advertises how stuck you could be
 * and turns asking one question into reading a menu, so only the rungs that
 * have actually been opened are on screen.
 *
 * The first rung no longer costs anything (progress.ts, `FREE_HINTS`). It is
 * a question by contract, and a question is not an answer to charge for —
 * pricing it priced *asking*, which is the one thing a learner who is unsure
 * whether they are stuck will not do. That argument used to be half of a
 * pair: a free mentor sat above this and read the workspace on every press,
 * so the amount of help somebody had to pay for before finding out what was
 * wrong was none. That mentor is retired (docs/mentor.md) and the free rung
 * is what carries the argument on its own now, which is why it matters more
 * than it did that this rung stays a question.
 */
export function HintLadder({ hints, hintCount, freeHints = 0, freeThrough = 0, busy, onAsk }: HintLadderProps) {
  const s = useStrings().progress.hints;
  const used = hints.length;
  const next = used + 1;
  // What the stars are actually charged against. Two kinds of rung are not:
  // the standing free first one, and any the app pushed at a learner who was
  // already stuck. Neither is help they went looking for. This line has to
  // say the same thing the clear overlay will, or the product has quietly
  // lied about the price twice — so both read the same figure, computed once
  // in Play and handed to each of them (progress.ts, `FREE_HINTS`).
  const paid = Math.max(0, used - freeHints);
  const exhausted = next > hintCount;
  const prevUsed = useRef(used);

  useEffect(() => {
    if (used > prevUsed.current) sound.hint();
    prevUsed.current = used;
  }, [used]);

  return (
    <>
      {used > 0 && (
        <ol className="ladder">
          <AnimatePresence initial={false}>
            {hints.map((hint) => (
              <motion.li
                key={hint.level}
                className="rung is-open"
                initial={prefersReducedMotion() ? false : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={ease}
              >
                <div className="rung-text prose" dangerouslySetInnerHTML={md(hint.text)} />
              </motion.li>
            ))}
          </AnimatePresence>
        </ol>
      )}

      <Button variant="quiet" wide onClick={onAsk} disabled={busy || exhausted}>
        {exhausted ? s.exhausted : next === hintCount ? s.showAnswer : used === 0 ? s.showFirst : s.showAnother}
      </Button>

      {/* Both cost lines have the stars set in the middle of them, and where
          the middle is is part of the translation — in Arabic the price and
          the verb do not fall in the English order. So the catalogue returns
          the sentence as parts with the element already in place and this
          renders it, rather than writing `{text}<Stars/>{rest}` and deciding
          for every language at once. */}
      {!exhausted && (
        <p className="hint-cost">
          {paid === 0 ? (
            // `used > 0`, not `freeHints > 0`. The allowance is now standing
            // rather than earned, so it is never zero, and the clause it
            // guards — "what you have been shown is on the house" — is about
            // rungs actually shown. Passing the allowance would print it to
            // somebody who has been shown nothing.
            <Parts of={s.worthWithoutHelp(<Stars value={3} />, used > 0)} />
          ) : (
            // `starsFor`, not a copy of its thresholds. This line had its own
            // `paid < 3 ? 2 : 1` and was a second opinion on the price: the
            // moment progress.ts moved the last rung's cost, this panel would
            // have gone on quoting the old one, which is the same lie the
            // free first rung was introduced to stop telling.
            <Parts of={s.nowWorth(<Stars value={starsFor(paid)} />)} />
          )}
          {/* Said only before the first rung is opened, which is the moment
              the hesitation this feature exists for actually happens. After
              that the sentence above is already telling the same truth.

              When the level has recognized the state the workspace is in and
              that state makes more than the standing first rung free, say so
              instead — it is a bigger and more surprising offer, and it is the
              one the learner has not been told about anywhere else. Saying
              both would be two sentences about the same price. */}
          {used === 0 && <> {freeThrough > FREE_HINTS ? s.freeThrough(freeThrough) : s.firstFree}</>}
        </p>
      )}
    </>
  );
}

export default HintLadder;
